import _asyncToGenerator from '@babel/runtime/helpers/asyncToGenerator';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _regeneratorRuntime from '@babel/runtime/regenerator';
import { r as radiansFromDegrees } from '../../Common/Core/Math/index.js';
import { FieldAssociations } from '../../Common/DataModel/DataSet/Constants.js';
import macro from '../../macros.js';
import vtkSelectionNode from '../../Common/DataModel/SelectionNode.js';
import WidgetManagerConst from './WidgetManager/Constants.js';
import vtkSVGRepresentation from '../SVG/SVGRepresentation.js';
import { WIDGET_PRIORITY } from './AbstractWidget/Constants.js';
import { diff } from './WidgetManager/vdom.js';

var ViewTypes = WidgetManagerConst.ViewTypes,
    RenderingTypes = WidgetManagerConst.RenderingTypes,
    CaptureOn = WidgetManagerConst.CaptureOn;
var vtkErrorMacro = macro.vtkErrorMacro,
    vtkWarningMacro = macro.vtkWarningMacro;
var createSvgElement = vtkSVGRepresentation.createSvgElement,
    createSvgDomElement = vtkSVGRepresentation.createSvgDomElement;
var viewIdCount = 1; // ----------------------------------------------------------------------------
// Helper
// ----------------------------------------------------------------------------

function extractRenderingComponents(renderer) {
  var camera = renderer.getActiveCamera();
  var renderWindow = renderer.getRenderWindow();
  var interactor = renderWindow.getInteractor();
  var apiSpecificRenderWindow = interactor.getView();
  return {
    renderer: renderer,
    renderWindow: renderWindow,
    interactor: interactor,
    apiSpecificRenderWindow: apiSpecificRenderWindow,
    camera: camera
  };
} // ----------------------------------------------------------------------------

function createSvgRoot(id) {
  var svgRoot = createSvgDomElement('svg');
  svgRoot.setAttribute('version', '1.1');
  svgRoot.setAttribute('baseProfile', 'full');
  return svgRoot;
} // ----------------------------------------------------------------------------
// vtkWidgetManager methods
// ----------------------------------------------------------------------------


function vtkWidgetManager(publicAPI, model) {
  if (!model.viewId) {
    model.viewId = "view-".concat(viewIdCount++);
  }

  model.classHierarchy.push('vtkWidgetManager');
  var propsWeakMap = new WeakMap();
  var widgetToSvgMap = new WeakMap();
  var svgVTrees = new WeakMap();
  var subscriptions = []; // --------------------------------------------------------------------------
  // Internal variable
  // --------------------------------------------------------------------------

  model.svgRoot = createSvgRoot(model.viewId); // --------------------------------------------------------------------------
  // API internal
  // --------------------------------------------------------------------------

  function updateWidgetWeakMap(widget) {
    var representations = widget.getRepresentations();

    for (var i = 0; i < representations.length; i++) {
      var representation = representations[i];
      var origin = {
        widget: widget,
        representation: representation
      };
      var actors = representation.getActors();

      for (var j = 0; j < actors.length; j++) {
        var actor = actors[j];
        propsWeakMap.set(actor, origin);
      }
    }
  }

  function getViewWidget(widget) {
    return widget && (widget.isA('vtkAbstractWidget') ? widget : widget.getWidgetForView({
      viewId: model.viewId
    }));
  } // --------------------------------------------------------------------------
  // internal SVG API
  // --------------------------------------------------------------------------


  var pendingSvgRenders = new WeakMap();

  function enableSvgLayer() {
    var container = model._apiSpecificRenderWindow.getReferenceByName('el');

    var canvas = model._apiSpecificRenderWindow.getCanvas();

    container.insertBefore(model.svgRoot, canvas.nextSibling);
    var containerStyles = window.getComputedStyle(container);

    if (containerStyles.position === 'static') {
      container.style.position = 'relative';
    }
  }

  function disableSvgLayer() {
    var container = model._apiSpecificRenderWindow.getReferenceByName('el');

    container.removeChild(model.svgRoot);
  }

  function removeFromSvgLayer(viewWidget) {
    var group = widgetToSvgMap.get(viewWidget);

    if (group) {
      widgetToSvgMap.delete(viewWidget);
      svgVTrees.delete(viewWidget);
      model.svgRoot.removeChild(group);
    }
  }

  function setSvgSize() {
    var _model$_apiSpecificRe = model._apiSpecificRenderWindow.getViewportSize(model._renderer),
        _model$_apiSpecificRe2 = _slicedToArray(_model$_apiSpecificRe, 2),
        cwidth = _model$_apiSpecificRe2[0],
        cheight = _model$_apiSpecificRe2[1];

    var ratio = window.devicePixelRatio || 1;
    var bwidth = String(cwidth / ratio);
    var bheight = String(cheight / ratio);
    var viewBox = "0 0 ".concat(cwidth, " ").concat(cheight);
    var origWidth = model.svgRoot.getAttribute('width');
    var origHeight = model.svgRoot.getAttribute('height');
    var origViewBox = model.svgRoot.getAttribute('viewBox');

    if (origWidth !== bwidth) {
      model.svgRoot.setAttribute('width', bwidth);
    }

    if (origHeight !== bheight) {
      model.svgRoot.setAttribute('height', bheight);
    }

    if (origViewBox !== viewBox) {
      model.svgRoot.setAttribute('viewBox', viewBox);
    }
  }

  function setSvgRootStyle() {
    var viewport = model._renderer.getViewport().map(function (v) {
      return v * 100;
    });

    model.svgRoot.setAttribute('style', "position: absolute; left: ".concat(viewport[0], "%; top: ").concat(100 - viewport[3], "%; width: ").concat(viewport[2] - viewport[0], "%; height: ").concat(viewport[3] - viewport[1], "%;"));
  }

  function updateSvg() {
    if (model.useSvgLayer) {
      var _loop = function _loop(i) {
        var widget = model.widgets[i];
        var svgReps = widget.getRepresentations().filter(function (r) {
          return r.isA('vtkSVGRepresentation');
        });
        var pendingContent = [];

        if (widget.getVisibility()) {
          pendingContent = svgReps.filter(function (r) {
            return r.getVisibility();
          }).map(function (r) {
            return r.render();
          });
        }

        var promise = Promise.all(pendingContent);
        var renders = pendingSvgRenders.get(widget) || [];
        renders.push(promise);
        pendingSvgRenders.set(widget, renders);
        promise.then(function (vnodes) {
          var pendingRenders = pendingSvgRenders.get(widget) || [];
          var idx = pendingRenders.indexOf(promise);

          if (model.deleted || widget.isDeleted() || idx === -1) {
            return;
          } // throw away previous renders


          pendingRenders = pendingRenders.slice(idx + 1);
          pendingSvgRenders.set(widget, pendingRenders);
          var oldVTree = svgVTrees.get(widget);
          var newVTree = createSvgElement('g');

          for (var ni = 0; ni < vnodes.length; ni++) {
            newVTree.appendChild(vnodes[ni]);
          }

          var widgetGroup = widgetToSvgMap.get(widget);
          var node = widgetGroup;
          var patchFns = diff(oldVTree, newVTree);

          for (var j = 0; j < patchFns.length; j++) {
            node = patchFns[j](node);
          }

          if (!widgetGroup && node) {
            // add
            model.svgRoot.appendChild(node);
            widgetToSvgMap.set(widget, node);
          } else if (widgetGroup && !node) {
            // delete
            widgetGroup.remove();
            widgetToSvgMap.delete(widget);
          }

          svgVTrees.set(widget, newVTree);
        });
      };

      for (var i = 0; i < model.widgets.length; i++) {
        _loop(i);
      }
    }
  } // --------------------------------------------------------------------------
  // Widget scaling
  // --------------------------------------------------------------------------


  function updateDisplayScaleParams() {
    var _apiSpecificRenderWindow = model._apiSpecificRenderWindow,
        _camera = model._camera,
        _renderer = model._renderer;

    if (_renderer && _apiSpecificRenderWindow && _camera) {
      var _apiSpecificRenderWin = _apiSpecificRenderWindow.getSize(),
          _apiSpecificRenderWin2 = _slicedToArray(_apiSpecificRenderWin, 2),
          rwW = _apiSpecificRenderWin2[0],
          rwH = _apiSpecificRenderWin2[1];

      var _renderer$getViewport = _renderer.getViewport(),
          _renderer$getViewport2 = _slicedToArray(_renderer$getViewport, 4),
          vxmin = _renderer$getViewport2[0],
          vymin = _renderer$getViewport2[1],
          vxmax = _renderer$getViewport2[2],
          vymax = _renderer$getViewport2[3];

      var rendererPixelDims = [rwW * (vxmax - vxmin), rwH * (vymax - vymin)];

      var cameraPosition = _camera.getPosition();

      var cameraDir = _camera.getDirectionOfProjection();

      var isParallel = _camera.getParallelProjection();

      var dispHeightFactor = isParallel ? 2 * _camera.getParallelScale() : 2 * Math.tan(radiansFromDegrees(_camera.getViewAngle()) / 2);
      model.widgets.forEach(function (w) {
        w.getNestedProps().forEach(function (r) {
          if (r.getScaleInPixels()) {
            r.setDisplayScaleParams({
              dispHeightFactor: dispHeightFactor,
              cameraPosition: cameraPosition,
              cameraDir: cameraDir,
              isParallel: isParallel,
              rendererPixelDims: rendererPixelDims
            });
          }
        });
      });
    }
  } // --------------------------------------------------------------------------
  // API public
  // --------------------------------------------------------------------------


  function updateSelection(_x) {
    return _updateSelection.apply(this, arguments);
  }

  function _updateSelection() {
    _updateSelection = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(_ref) {
      var position, _yield$publicAPI$getS, requestCount, selectedState, representation, widget, i, w;

      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              position = _ref.position;
              model._selectionInProgress = true;
              _context2.next = 4;
              return publicAPI.getSelectedDataForXY(position.x, position.y);

            case 4:
              _yield$publicAPI$getS = _context2.sent;
              requestCount = _yield$publicAPI$getS.requestCount;
              selectedState = _yield$publicAPI$getS.selectedState;
              representation = _yield$publicAPI$getS.representation;
              widget = _yield$publicAPI$getS.widget;
              model._selectionInProgress = false;

              if (!requestCount) {
                _context2.next = 12;
                break;
              }

              return _context2.abrupt("return");

            case 12:
              // Default cursor behavior
              model._apiSpecificRenderWindow.setCursor(widget ? 'pointer' : 'default');

              if (model.widgetInFocus === widget && widget.hasFocus()) {
                widget.activateHandle({
                  selectedState: selectedState,
                  representation: representation
                }); // Ken FIXME

                model._interactor.render();

                model._interactor.render();
              } else {
                for (i = 0; i < model.widgets.length; i++) {
                  w = model.widgets[i];

                  if (w === widget && w.getNestedPickable()) {
                    w.activateHandle({
                      selectedState: selectedState,
                      representation: representation
                    });
                    model.activeWidget = w;
                  } else {
                    w.deactivateAllHandles();
                  }
                } // Ken FIXME


                model._interactor.render();

                model._interactor.render();
              }

            case 14:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));
    return _updateSelection.apply(this, arguments);
  }

  var handleEvent = function handleEvent(eventName) {
    var guard = false;
    return function (callData) {
      if (guard || model.isAnimating || !model.pickingEnabled || model._selectionInProgress) {
        return macro.VOID;
      }

      var updatePromise = updateSelection(callData);
      macro.measurePromiseExecution(updatePromise, function (elapsed) {
        // 100ms is deemed fast enough. Anything higher can degrade usability.
        if (elapsed > 100) {
          macro.vtkWarningMacro("vtkWidgetManager updateSelection() took ".concat(elapsed, "ms on ").concat(eventName));
        }
      });
      updatePromise.then(function () {
        if (model._interactor) {
          // re-trigger the event, ignoring our own handler
          guard = true;

          model._interactor["invoke".concat(eventName)](callData);

          guard = false;
        }
      });
      return macro.EVENT_ABORT;
    };
  };

  function updateWidgetForRender(w) {
    w.updateRepresentationForRender(model.renderingType);
  }

  function renderPickingBuffer() {
    model.renderingType = RenderingTypes.PICKING_BUFFER;
    model.widgets.forEach(updateWidgetForRender);
  }

  function renderFrontBuffer() {
    model.renderingType = RenderingTypes.FRONT_BUFFER;
    model.widgets.forEach(updateWidgetForRender);
  }

  function captureBuffers(_x2, _x3, _x4, _x5) {
    return _captureBuffers.apply(this, arguments);
  }

  function _captureBuffers() {
    _captureBuffers = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(x1, y1, x2, y2) {
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              if (!model._captureInProgress) {
                _context3.next = 2;
                break;
              }

              return _context3.abrupt("return");

            case 2:
              model._captureInProgress = true;
              renderPickingBuffer();
              model._capturedBuffers = null;
              _context3.next = 7;
              return model._selector.getSourceDataAsync(model._renderer, x1, y1, x2, y2);

            case 7:
              model._capturedBuffers = _context3.sent;
              model.previousSelectedData = null;
              renderFrontBuffer();
              model._captureInProgress = false;

            case 11:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }));
    return _captureBuffers.apply(this, arguments);
  }

  publicAPI.enablePicking = function () {
    model.pickingEnabled = true;
    publicAPI.renderWidgets();
  };

  publicAPI.renderWidgets = function () {
    if (model.pickingEnabled && model.captureOn === CaptureOn.MOUSE_RELEASE) {
      var _model$_apiSpecificRe3 = model._apiSpecificRenderWindow.getSize(),
          _model$_apiSpecificRe4 = _slicedToArray(_model$_apiSpecificRe3, 2),
          w = _model$_apiSpecificRe4[0],
          h = _model$_apiSpecificRe4[1];

      captureBuffers(0, 0, w, h);
    }

    renderFrontBuffer();
    publicAPI.modified();
  };

  publicAPI.disablePicking = function () {
    model.pickingEnabled = false;
  };

  publicAPI.setRenderer = function (renderer) {
    var renderingComponents = extractRenderingComponents(renderer);
    Object.assign(model, renderingComponents);
    macro.moveToProtected({}, model, Object.keys(renderingComponents));

    while (subscriptions.length) {
      subscriptions.pop().unsubscribe();
    }

    model._selector = model._apiSpecificRenderWindow.createSelector();

    model._selector.setFieldAssociation(FieldAssociations.FIELD_ASSOCIATION_POINTS);

    subscriptions.push(model._interactor.onRenderEvent(updateSvg));
    subscriptions.push(renderer.onModified(setSvgRootStyle));
    setSvgRootStyle();
    subscriptions.push(model._apiSpecificRenderWindow.onModified(setSvgSize));
    setSvgSize();
    subscriptions.push(model._apiSpecificRenderWindow.onModified(updateDisplayScaleParams));
    subscriptions.push(model._camera.onModified(updateDisplayScaleParams));
    updateDisplayScaleParams();
    subscriptions.push(model._interactor.onStartAnimation(function () {
      model.isAnimating = true;
    }));
    subscriptions.push(model._interactor.onEndAnimation(function () {
      model.isAnimating = false;
      publicAPI.renderWidgets();
    }));
    subscriptions.push(model._interactor.onMouseMove(handleEvent('MouseMove')));
    subscriptions.push(model._interactor.onLeftButtonPress(handleEvent('LeftButtonPress'), // stay after widgets, but before default of 0
    WIDGET_PRIORITY / 2));
    publicAPI.modified();

    if (model.pickingEnabled) {
      publicAPI.enablePicking();
    }

    if (model.useSvgLayer) {
      enableSvgLayer();
    }
  };

  function addWidgetInternal(viewWidget) {
    viewWidget.setWidgetManager(publicAPI);
    updateWidgetWeakMap(viewWidget);
    updateDisplayScaleParams(); // Register to renderer

    model._renderer.addActor(viewWidget);
  }

  publicAPI.addWidget = function (widget, viewType, initialValues) {
    if (!model._renderer) {
      vtkErrorMacro('Widget manager MUST BE link to a view before registering widgets');
      return null;
    }

    var viewId = model.viewId,
        _renderer = model._renderer;
    var w = widget.getWidgetForView({
      viewId: viewId,
      renderer: _renderer,
      viewType: viewType || ViewTypes.DEFAULT,
      initialValues: initialValues
    });

    if (w != null && model.widgets.indexOf(w) === -1) {
      model.widgets.push(w);
      addWidgetInternal(w);
      publicAPI.modified();
    }

    return w;
  };

  function removeWidgetInternal(viewWidget) {
    model._renderer.removeActor(viewWidget);

    removeFromSvgLayer(viewWidget);
    viewWidget.delete();
  }

  function onWidgetRemoved() {
    model._renderer.getRenderWindow().getInteractor().render();

    publicAPI.renderWidgets();
  }

  publicAPI.removeWidgets = function () {
    model.widgets.forEach(removeWidgetInternal);
    model.widgets = [];
    model.widgetInFocus = null;
    onWidgetRemoved();
  };

  publicAPI.removeWidget = function (widget) {
    var viewWidget = getViewWidget(widget);
    var index = model.widgets.indexOf(viewWidget);

    if (index !== -1) {
      model.widgets.splice(index, 1);
      var isWidgetInFocus = model.widgetInFocus === viewWidget;

      if (isWidgetInFocus) {
        publicAPI.releaseFocus();
      }

      removeWidgetInternal(viewWidget);
      onWidgetRemoved();
    }
  };

  publicAPI.getSelectedDataForXY = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(x, y) {
      var i, widget, hoveredSVGReps, selection, capturedRegion;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              model.selections = null;

              if (!model.pickingEnabled) {
                _context.next = 24;
                break;
              }

              i = 0;

            case 3:
              if (!(i < model.widgets.length)) {
                _context.next = 16;
                break;
              }

              widget = model.widgets[i];
              hoveredSVGReps = widget.getRepresentations().filter(function (r) {
                return r.isA('vtkSVGRepresentation') && r.getHover() != null;
              });

              if (!hoveredSVGReps.length) {
                _context.next = 13;
                break;
              }

              selection = vtkSelectionNode.newInstance();
              selection.getProperties().compositeID = hoveredSVGReps[0].getHover();
              selection.getProperties().widget = widget;
              selection.getProperties().representation = hoveredSVGReps[0];
              model.selections = [selection];
              return _context.abrupt("return", publicAPI.getSelectedData());

            case 13:
              ++i;
              _context.next = 3;
              break;

            case 16:
              if (!(!model._capturedBuffers || model.captureOn === CaptureOn.MOUSE_MOVE)) {
                _context.next = 19;
                break;
              }

              _context.next = 19;
              return captureBuffers(x, y, x, y);

            case 19:
              // or do we need a pixel that is outside the last capture?
              capturedRegion = model._capturedBuffers.area;

              if (!(x < capturedRegion[0] || x > capturedRegion[2] || y < capturedRegion[1] || y > capturedRegion[3])) {
                _context.next = 23;
                break;
              }

              _context.next = 23;
              return captureBuffers(x, y, x, y);

            case 23:
              model.selections = model._capturedBuffers.generateSelection(x, y, x, y);

            case 24:
              return _context.abrupt("return", publicAPI.getSelectedData());

            case 25:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function (_x6, _x7) {
      return _ref2.apply(this, arguments);
    };
  }();

  publicAPI.updateSelectionFromXY = function (x, y) {
    vtkWarningMacro('updateSelectionFromXY is deprecated, please use getSelectedDataForXY');

    if (model.pickingEnabled) {
      // First pick SVG representation
      for (var i = 0; i < model.widgets.length; ++i) {
        var widget = model.widgets[i];
        var hoveredSVGReps = widget.getRepresentations().filter(function (r) {
          return r.isA('vtkSVGRepresentation') && r.getHover() != null;
        });

        if (hoveredSVGReps.length) {
          var selection = vtkSelectionNode.newInstance();
          selection.getProperties().compositeID = hoveredSVGReps[0].getHover();
          selection.getProperties().widget = widget;
          selection.getProperties().representation = hoveredSVGReps[0];
          model.selections = [selection];
          return;
        }
      } // Then pick regular representations.


      if (model.captureOn === CaptureOn.MOUSE_MOVE) {
        captureBuffers(x, y, x, y);
      }
    }
  };

  publicAPI.updateSelectionFromMouseEvent = function (event) {
    vtkWarningMacro('updateSelectionFromMouseEvent is deprecated, please use getSelectedDataForXY');
    var pageX = event.pageX,
        pageY = event.pageY;

    var _model$_apiSpecificRe5 = model._apiSpecificRenderWindow.getCanvas().getBoundingClientRect(),
        top = _model$_apiSpecificRe5.top,
        left = _model$_apiSpecificRe5.left,
        height = _model$_apiSpecificRe5.height;

    var x = pageX - left;
    var y = height - (pageY - top);
    publicAPI.updateSelectionFromXY(x, y);
  };

  publicAPI.getSelectedData = function () {
    if (!model.selections || !model.selections.length) {
      model.previousSelectedData = null;
      return {};
    }

    var _model$selections$0$g = model.selections[0].getProperties(),
        propID = _model$selections$0$g.propID,
        compositeID = _model$selections$0$g.compositeID,
        prop = _model$selections$0$g.prop;

    var _model$selections$0$g2 = model.selections[0].getProperties(),
        widget = _model$selections$0$g2.widget,
        representation = _model$selections$0$g2.representation; // prop is undefined for SVG representation, widget is undefined for handle
    // representation.


    if (model.previousSelectedData && model.previousSelectedData.prop === prop && model.previousSelectedData.widget === widget && model.previousSelectedData.compositeID === compositeID) {
      model.previousSelectedData.requestCount++;
      return model.previousSelectedData;
    }

    if (propsWeakMap.has(prop)) {
      var props = propsWeakMap.get(prop);
      widget = props.widget;
      representation = props.representation;
    }

    if (widget && representation) {
      var selectedState = representation.getSelectedState(prop, compositeID);
      model.previousSelectedData = {
        requestCount: 0,
        propID: propID,
        compositeID: compositeID,
        prop: prop,
        widget: widget,
        representation: representation,
        selectedState: selectedState
      };
      return model.previousSelectedData;
    }

    model.previousSelectedData = null;
    return {};
  };

  publicAPI.grabFocus = function (widget) {
    var viewWidget = getViewWidget(widget);

    if (model.widgetInFocus && model.widgetInFocus !== viewWidget) {
      model.widgetInFocus.loseFocus();
    }

    model.widgetInFocus = viewWidget;

    if (model.widgetInFocus) {
      model.widgetInFocus.grabFocus();
    }
  };

  publicAPI.releaseFocus = function () {
    return publicAPI.grabFocus(null);
  };

  publicAPI.setUseSvgLayer = function (useSvgLayer) {
    if (useSvgLayer !== model.useSvgLayer) {
      model.useSvgLayer = useSvgLayer;

      if (model._renderer) {
        if (useSvgLayer) {
          enableSvgLayer(); // force a render so svg widgets can be drawn

          updateSvg();
        } else {
          disableSvgLayer();
        }
      }

      return true;
    }

    return false;
  };

  var superDelete = publicAPI.delete;

  publicAPI.delete = function () {
    while (subscriptions.length) {
      subscriptions.pop().unsubscribe();
    }

    superDelete();
  };
} // ----------------------------------------------------------------------------
// Object factory
// ----------------------------------------------------------------------------


var DEFAULT_VALUES = {
  // _camera: null,
  // _selector: null,
  viewId: null,
  widgets: [],
  renderer: null,
  viewType: ViewTypes.DEFAULT,
  isAnimating: false,
  pickingEnabled: true,
  selections: null,
  previousSelectedData: null,
  widgetInFocus: null,
  useSvgLayer: true,
  captureOn: CaptureOn.MOUSE_MOVE
}; // ----------------------------------------------------------------------------

function extend(publicAPI, model) {
  var initialValues = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  Object.assign(model, DEFAULT_VALUES, initialValues);
  macro.obj(publicAPI, model);
  macro.setGet(publicAPI, model, ['captureOn', {
    type: 'enum',
    name: 'viewType',
    enum: ViewTypes
  }]);
  macro.get(publicAPI, model, ['selections', 'widgets', 'viewId', 'pickingEnabled', 'useSvgLayer']); // Object specific methods

  vtkWidgetManager(publicAPI, model);
} // ----------------------------------------------------------------------------

var newInstance = macro.newInstance(extend, 'vtkWidgetManager'); // ----------------------------------------------------------------------------

var vtkWidgetManager$1 = {
  newInstance: newInstance,
  extend: extend,
  Constants: WidgetManagerConst
};

export { vtkWidgetManager$1 as default, extend, extractRenderingComponents, newInstance };
